import * as React from 'react';
import { FixMeLater } from '../../utils';
import { Styler, StylerBaseProps } from '../Styler';
import { ColorOption, ResponsiveStyleValue, useComponentDefaultProps } from '../../theming';
import { DividerOrientation } from './Divider.types';
import { useStyles } from './Divider.styles';
import { TypographyBody, TypographyBodyProps } from '../Typography';

export interface DividerProps extends StylerBaseProps {
    /**
     * The orientation of the divider.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default horizontal
     */
    orientation?: ResponsiveStyleValue<DividerOrientation>;

    /**
     * The color of the divider.
     * @default divider
     */
    color?: ColorOption;

    /**
     * Text that will be positioned along the divider. Only applicable when `orientation` is `horizontal`.
     * @default 1
     */
    label?: React.ReactNode;

    /**
     * The thickness of the divider in pixels.
     * @default 1
     */
    thickness?: ResponsiveStyleValue<number>;

    /**
     * The appearance of the divider.
     * @default solid
     */
    variant?: 'solid' | 'dashed' | 'dotted';

    /**
     * Additional properties applied to the label component.
     */
    LabelProps?: TypographyBodyProps;
}

const defaultProps: Partial<DividerProps> = {
    orientation: 'horizontal',
    color: 'divider',
    thickness: 1,
    variant: 'solid',
};

/**
 * Divider is a reusable component that represents a horizontal or vertical line that separates content. It can be used to divide sections of a page or to separate items in a list.
 * The component can be used as a simple divider or as a labeled divider, depending on the provided props. If the label prop is provided, the divider will have a label.
 * We can have various controls over the divider such as thickness, color, variant and orientation as described in the props section below.
 */

export const Divider: React.ForwardRefExoticComponent<DividerProps & React.RefAttributes<HTMLElement>> =
    React.forwardRef(function Divider(props: DividerProps, ref: React.ForwardedRef<FixMeLater>): React.JSX.Element {
        const {
            color = 'divider',
            orientation = 'horizontal',
            thickness = 1,
            variant = 'solid',
            label,
            LabelProps,
            ...other
        } = useComponentDefaultProps('Divider', defaultProps, props);

        const withLabel = !!label && orientation === 'horizontal';

        const { styles } = useStyles({ color, orientation, thickness, variant, withLabel });

        return (
            <Styler ref={ref} baseStyles={styles.root} role="separator" {...other}>
                {withLabel && (
                    <TypographyBody data-label={true} component="div" {...LabelProps}>
                        {label}
                    </TypographyBody>
                )}
            </Styler>
        );
    });
